import { useState, useRef, useEffect, type ReactNode } from 'react';
import { useAuth } from '@/auth/AuthContext';
import { Link, useLocation } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import {
  LayoutDashboard, Users, Building2,
  UserCheck, CreditCard, LogOut, Menu, ChevronRight, ChevronLeft, Shield, ClipboardCheck, BarChart3, ArrowLeftRight,
  Receipt, AlertCircle, ScrollText, KeyRound, UserX,
} from 'lucide-react';
import { ChangePasswordModal } from '@/components/ChangePasswordModal';
import { AccountDeletionModal } from '@/components/AccountDeletionModal';
import type { Tenant, UserRole } from '@/api/types';
import client from '@/api/client';
import { NotificationBell, NotificationToasts, useToasts } from '@/components/notifications/NotificationBell';
import { usePushNotifications } from '@/hooks/usePushNotifications';
import { LanguageSelector } from '@/components/LanguageSelector';
import { ThemeSelector } from '@/components/ThemeSelector';

interface NavItem { labelKey: string; to: string; icon: React.ReactNode }

const ADMIN_NAV: NavItem[] = [
  { labelKey: 'nav.dashboard', to: '/admin/dashboard', icon: <LayoutDashboard size={18} /> },
  { labelKey: 'nav.approvals', to: '/admin/approvals', icon: <ClipboardCheck size={18} /> },
  { labelKey: 'nav.owners',    to: '/admin/owners',    icon: <UserCheck size={18} /> },
  { labelKey: 'nav.hostels',   to: '/admin/hostels',   icon: <Building2 size={18} /> },
  { labelKey: 'nav.users',     to: '/admin/users',     icon: <Users size={18} /> },
];

const OWNER_NAV: NavItem[] = [
  { labelKey: 'nav.dashboard',   to: '/owner/dashboard',    icon: <LayoutDashboard size={18} /> },
  { labelKey: 'nav.analytics',   to: '/owner/analytics',    icon: <BarChart3 size={18} /> },
  { labelKey: 'nav.hostels',     to: '/owner/hostels',      icon: <Building2 size={18} /> },
  { labelKey: 'nav.tenants',     to: '/owner/tenants',      icon: <UserCheck size={18} /> },
  { labelKey: 'nav.rent',        to: '/owner/rent',         icon: <CreditCard size={18} /> },
  { labelKey: 'nav.swapBeds',    to: '/owner/swap-beds',    icon: <ArrowLeftRight size={18} /> },
  { labelKey: 'nav.supervisors', to: '/owner/supervisors',  icon: <Shield size={18} /> },
];

const SUPERVISOR_NAV: NavItem[] = [
  { labelKey: 'nav.dashboard', to: '/supervisor/dashboard', icon: <LayoutDashboard size={18} /> },
  { labelKey: 'nav.analytics', to: '/supervisor/analytics', icon: <BarChart3 size={18} /> },
  { labelKey: 'nav.hostels',   to: '/supervisor/hostels',   icon: <Building2 size={18} /> },
  { labelKey: 'nav.tenants',   to: '/supervisor/tenants',   icon: <UserCheck size={18} /> },
  { labelKey: 'nav.rent',      to: '/supervisor/rent',      icon: <CreditCard size={18} /> },
  { labelKey: 'nav.swapBeds',  to: '/supervisor/swap-beds', icon: <ArrowLeftRight size={18} /> },
];

const TENANT_NAV: NavItem[] = [
  { labelKey: 'nav.dashboard',   to: '/tenant/dashboard',   icon: <LayoutDashboard size={18} /> },
  { labelKey: 'tenant.payments', to: '/tenant/payments',    icon: <Receipt size={18} /> },
  { labelKey: 'nav.complaints',  to: '/tenant/complaints',  icon: <AlertCircle size={18} /> },
  { labelKey: 'hostel.rules',    to: '/tenant/rules',       icon: <ScrollText size={18} /> },
];

const NAV: Record<UserRole, NavItem[]> = {
  ADMIN:        ADMIN_NAV,
  HOSTEL_OWNER: OWNER_NAV,
  SUPERVISOR:   SUPERVISOR_NAV,
  TENANT:       TENANT_NAV,
};

const ROLE_KEY: Record<UserRole, string> = {
  ADMIN:        'role.admin',
  HOSTEL_OWNER: 'role.owner',
  SUPERVISOR:   'role.supervisor',
  TENANT:       'role.tenant',
};

export function AppShell({ children }: { children: ReactNode }) {
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [deletionModalOpen, setDeletionModalOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem('sidebarCollapsed') === 'true');
  const profileRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  function toggleCollapsed() {
    setCollapsed(prev => {
      const next = !prev;
      localStorage.setItem('sidebarCollapsed', String(next));
      return next;
    });
  }

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (profileRef.current && !profileRef.current.contains(e.target as Node)) {
        setProfileOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);
  const { toasts, addToast, dismissToast } = useToasts();

  usePushNotifications(addToast);

  // Tenants not yet tagged to a hostel (or fully checked out) don't have Payments/Complaints/
  // Rules & Terms available yet — those only apply once an active/reserved admission exists.
  const { data: tenantPortalRecords } = useQuery<Tenant[]>({
    queryKey: ['tenant-portal-me'],
    queryFn: () => client.get('/api/tenant-portal/me').then(r => r as unknown as Tenant[]),
    enabled: user?.role === 'TENANT',
  });

  if (!user) return null;

  const hasActiveHostel = (tenantPortalRecords ?? []).some(t => t.status === 'ACTIVE' || t.status === 'RESERVED');
  const nav = user.role === 'TENANT' && !hasActiveHostel
    ? NAV.TENANT.filter(item => item.labelKey === 'nav.dashboard')
    : NAV[user.role];

  const NavLinks = ({ collapsed: isCollapsed = false }: { collapsed?: boolean }) => (
    <nav className="flex-1 py-4 space-y-1 px-2">
      {nav.map(item => {
        const active = location.pathname === item.to || location.pathname.startsWith(item.to + '/');
        return (
          <Link
            key={item.to}
            to={item.to}
            onClick={() => setSidebarOpen(false)}
            title={isCollapsed ? t(item.labelKey) : undefined}
            className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition
              ${isCollapsed ? 'justify-center' : ''}
              ${active
                ? 'bg-chrome-sidebar-active text-chrome-sidebar-active-foreground'
                : 'text-chrome-sidebar-muted hover:bg-chrome-sidebar-hover hover:text-chrome-sidebar-foreground'
              }`}
          >
            {item.icon}
            {!isCollapsed && t(item.labelKey)}
            {!isCollapsed && active && <ChevronRight size={14} className="ml-auto" />}
          </Link>
        );
      })}
    </nav>
  );

  const SidebarContent = ({ collapsed: isCollapsed = false }: { collapsed?: boolean }) => (
    <div className="relative flex flex-col h-full bg-chrome-sidebar text-chrome-sidebar-foreground">
      <NavLinks collapsed={isCollapsed} />
      <div className="px-3 pb-4">
        <button
          onClick={logout}
          title={isCollapsed ? t('nav.signOut') : undefined}
          className={`flex items-center gap-3 w-full px-3 py-2.5 rounded-lg text-sm font-medium text-chrome-sidebar-muted hover:bg-chrome-sidebar-hover hover:text-chrome-sidebar-foreground transition ${isCollapsed ? 'justify-center' : ''}`}
        >
          <LogOut size={18} />
          {!isCollapsed && t('nav.signOut')}
        </button>
      </div>
      <button
        onClick={toggleCollapsed}
        title={isCollapsed ? t('nav.expand', 'Expand sidebar') : t('nav.collapse', 'Collapse sidebar')}
        className="hidden lg:flex items-center justify-center w-4 h-9 rounded-md bg-chrome-sidebar-active text-chrome-sidebar-active-foreground hover:opacity-90 absolute -right-2 top-1/2 -translate-y-1/2 z-10 shadow-md"
      >
        {isCollapsed ? <ChevronRight size={11} /> : <ChevronLeft size={11} />}
      </button>
    </div>
  );

  return (
    <div className="flex flex-col h-screen bg-chrome-page overflow-hidden">
      {/* Topbar — spans full width; the left segment doubles as the branding/logo slot and aligns with the sidebar column below it */}
      <header className="bg-chrome-topbar border-b border-chrome-topbar-border h-14 flex items-center shrink-0">
        <div
          className={`hidden lg:flex items-center gap-2.5 h-full border-r border-chrome-topbar-border shrink-0 transition-[width] duration-200 ${collapsed ? 'w-16 justify-center' : 'w-64 px-5'}`}
        >
          <div className="w-8 h-8 rounded-lg bg-chrome-badge flex items-center justify-center text-chrome-badge-foreground shrink-0">
            <Building2 size={18} />
          </div>
          {!collapsed && <span className="font-bold text-chrome-topbar-foreground text-base truncate">PG Management</span>}
        </div>

        <div className="flex items-center gap-3 flex-1 px-4">
          <button className="lg:hidden p-1.5 rounded-lg hover:bg-gray-100" onClick={() => setSidebarOpen(true)}>
            <Menu size={20} />
          </button>
          <div className="flex-1" />
          <ThemeSelector />
          <LanguageSelector variant="compact" />
          <NotificationBell />
          <div className="relative" ref={profileRef}>
            <button
              onClick={() => setProfileOpen(v => !v)}
              className="flex items-center gap-2.5 hover:opacity-90 transition"
            >
              <span className="hidden sm:block text-right leading-tight">
                <span className="block text-sm font-semibold text-chrome-topbar-foreground truncate max-w-[160px]">{user.fullName}</span>
                <span className="block text-xs text-gray-500">{t(ROLE_KEY[user.role])}</span>
              </span>
              <span className="w-8 h-8 shrink-0 rounded-full bg-chrome-badge flex items-center justify-center text-chrome-badge-foreground text-sm font-semibold">
                {user.fullName.charAt(0).toUpperCase()}
              </span>
            </button>
            {profileOpen && (
              <div className="absolute right-0 top-10 w-56 bg-chrome-page rounded-xl shadow-lg border border-chrome-topbar-border z-50 overflow-hidden">
                <div className="px-4 py-3 border-b border-chrome-topbar-border">
                  <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-0.5">{t(ROLE_KEY[user.role])}</p>
                  <p className="font-semibold text-gray-900 text-sm truncate">{user.fullName}</p>
                  <p className="text-xs text-gray-500 truncate">{user.email}</p>
                </div>
                <div className="p-2">
                  <button
                    onClick={() => { setProfileOpen(false); setChangePasswordOpen(true); }}
                    className="flex items-center gap-2 w-full px-3 py-2 rounded-lg text-sm text-gray-700 hover:bg-gray-100 transition"
                  >
                    <KeyRound size={15} />
                    {t('account.changePassword', 'Change Password')}
                  </button>
                  {(user.role === 'HOSTEL_OWNER' || user.role === 'SUPERVISOR' || user.role === 'TENANT') && (
                    <button
                      onClick={() => { setProfileOpen(false); setDeletionModalOpen(true); }}
                      className="flex items-center gap-2 w-full px-3 py-2 rounded-lg text-sm text-red-600 hover:bg-red-50 transition"
                    >
                      <UserX size={15} />
                      {t('account.deleteAccount', 'Delete Account')}
                    </button>
                  )}
                  <button
                    onClick={() => { setProfileOpen(false); logout(); }}
                    className="flex items-center gap-2 w-full px-3 py-2 rounded-lg text-sm text-red-600 hover:bg-red-50 transition"
                  >
                    <LogOut size={15} />
                    {t('nav.signOut')}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </header>

      <div className="flex flex-1 overflow-hidden">
        {/* Desktop sidebar */}
        <aside className={`hidden lg:flex flex-col shrink-0 transition-[width] duration-200 ${collapsed ? 'w-16' : 'w-64'}`}>
          <SidebarContent collapsed={collapsed} />
        </aside>

        {/* Mobile sidebar overlay */}
        {sidebarOpen && (
          <div className="fixed inset-0 z-40 lg:hidden">
            <div className="absolute inset-0 bg-black/50" onClick={() => setSidebarOpen(false)} />
            <div className="absolute left-0 top-0 bottom-0 w-64 z-50">
              <SidebarContent />
            </div>
          </div>
        )}

        {/* Foreground push toast container */}
        <NotificationToasts toasts={toasts} onDismiss={dismissToast} />

        {changePasswordOpen && <ChangePasswordModal onClose={() => setChangePasswordOpen(false)} />}
        {deletionModalOpen && <AccountDeletionModal role={user.role} onClose={() => setDeletionModalOpen(false)} onDeleted={logout} />}

        <main className="flex-1 overflow-y-auto p-4 lg:p-6">
          {children}
        </main>
      </div>
    </div>
  );
}
